import { Domain } from '@3nickels/data-modules';
import { useDeviceType } from '../../hooks/useDeviceType';
import i18next, { t } from 'i18next';
import { Card, CardContent, Typography } from '@mui/material';
import { Column, Row, Spacer } from '../../components';
import { Colors } from '../../themes';
import BulletedList from '../../components/BulletedList';
import { Trans } from 'react-i18next';
import { formatDecimalDollars } from '../../helpers/utilityFunctions';
import { PriceDisplay } from './PriceDisplay';
import InternalFreeTrialFootnote from './InternalFreeTrialFootnote';

interface PackageCardProps {
  pricebookBundle: Domain.PricebookBundle;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pricebookMetadata: any;
}

export const PackageCard: React.FC<PackageCardProps> = ({ pricebookBundle, pricebookMetadata }) => {
  const { language } = i18next;
  const { isMobile } = useDeviceType();
  const guidedChoice = pricebookBundle.pricebook.productType === 'GUIDED_CHOICE';
  const orgPays = pricebookBundle.pricebook.paidBy === Domain.PaidByEnum.ORG;
  const internalFreeTrial = pricebookBundle.pricebook.metadata?.internalFreeTrial === 'true';

  const promotion = pricebookBundle.prices.find((price) => price.introductionPeriodInDays > 1);
  const introPeriodInMonths = Math.floor((promotion?.introductionPeriodInDays ?? 0) / 30);

  return (
    <>
      <Card className='learnMorePackageCard' color='primary'>
        <CardContent className='learnMorePackageCardContents'>
          {isMobile ? (
            <Column style={{ alignItems: 'center' }}>
              <img
                src={pricebookBundle.pricebook.artUrl}
                alt='Product Art'
                style={{ height: '60px' }}
              />
              <Spacer height='xs' />

              <BulletedList
                children={pricebookBundle.pricebook.description}
                variant={guidedChoice ? 'p16OpenSans' : 'body1'}
                sx={{ color: guidedChoice ? Colors.secondaryDark : Colors.secondaryBase }}
              />
              <Spacer height='xs' />
              <Card variant='embeddedCard' style={{ width: '100%' }}>
                <CardContent className='centered-inner'>
                  <Column style={{ alignItems: 'center', textAlign: 'center' }}>
                    {pricebookMetadata ? (
                      <Typography variant='p16Bold' color='primary' textAlign='center'>
                        {t(pricebookMetadata.innerCard)}
                      </Typography>
                    ) : orgPays ? (
                      <Typography variant='p16Bold' color='primary'>
                        <Trans i18nKey='SponsoredByYourBROrganization' />
                      </Typography>
                    ) : (
                      <PriceDisplay
                        pricebookBundle={pricebookBundle}
                        variant='learnMore'
                        isGuidedChoice={guidedChoice}
                      />
                    )}
                  </Column>
                </CardContent>
              </Card>
            </Column>
          ) : (
            <Row style={{ justifyContent: 'space-between' }}>
              <img
                src={pricebookBundle.pricebook.artUrl}
                alt='Product Art'
                style={{ height: '70px', objectFit: 'contain' }}
              />
              <Column
                style={{
                  margin: '10px',
                  flex: 1,
                  alignSelf: 'center',
                  minWidth: '200px',
                  maxWidth: '400px',
                }}>
                <BulletedList
                  children={pricebookBundle.pricebook.description}
                  variant={guidedChoice ? 'p16OpenSans' : 'p14'}
                  color={guidedChoice ? Colors.secondaryDark : Colors.secondaryBase}
                />
              </Column>
              <Card
                style={{
                  alignSelf: 'center',
                  width: '30%',
                  flex: 1,
                  minWidth: '165px',
                  maxWidth: '250px',
                }}
                variant='embeddedCard'>
                <CardContent className='centered-inner'>
                  <Column style={{ alignItems: 'center', textAlign: 'center' }}>
                    {pricebookMetadata ? (
                      <Typography variant='p16Bold' color='primary' textAlign='center'>
                        {t(pricebookMetadata.innerCard)}
                      </Typography>
                    ) : orgPays ? (
                      <Typography variant='p16Bold' color='primary'>
                        <Trans i18nKey='SponsoredByYourBROrganization' />
                      </Typography>
                    ) : (
                      <PriceDisplay
                        pricebookBundle={pricebookBundle}
                        variant='learnMore'
                        isGuidedChoice={guidedChoice}
                      />
                    )}
                  </Column>
                </CardContent>
              </Card>
            </Row>
          )}
        </CardContent>
      </Card>
      <Spacer height='xxs' />
      {promotion && (
        <Typography variant={isMobile ? 'p12' : 'p14OpenSans'} color='secondary' display='inline'>
          *{' '}
          {t('AfterXMonthsAdviceSubscriptionAutoRenewsAt', {
            x: introPeriodInMonths,
            price: `${formatDecimalDollars(14.99, language)}/${t('month')}`,
          })}
        </Typography>
      )}
      {internalFreeTrial && (
        <InternalFreeTrialFootnote
          pricebookBundle={pricebookBundle}
          variant={isMobile ? 'p12' : 'p14OpenSans'}
          color='secondary'
          display='inline'
        />
      )}
    </>
  );
};
